import { ComponentTypes, Textbox, TextboxProps } from "@mcleod/components";
import { MobileDesignTokens } from "../tokens/MobileDesignTokens";

/**
 * The text field every `"type": "textbox"` in a .layout becomes when the layout is deserialized inside the mobile
 * app.  Defaults only; explicit layout / `appProps` values win.
 *
 * Rules applied (Material 3 / Apple HIG):
 *   - the tappable field is at least 48px tall (Material filled field 56, HIG 44);
 *   - text is 16px, which also stops WKWebView from zooming the page when the field gains focus;
 *   - 10px corner radius so fields read as the same family as the 12px buttons.
 *
 * As with AppButton, a deserialized field only learns its layout props after construction, so it applies the
 * defaults from `doAfterDeserialize()`; fields constructed with full props are handled in the constructor.
 */
export class AppTextbox extends Textbox {
    protected constructor(props?: Partial<TextboxProps>) {
        super(props);
        if (this._designer == null && !this.isDeserializing())
            AppTextbox.applyMobileDefaults(this, props);
    }

    override doAfterDeserialize(): void {
        super.doAfterDeserialize();
        if (this._designer == null)
            AppTextbox.applyMobileDefaults(this);
    }

    /**
     * Applies the token defaults for every sizing prop that was not set explicitly; `props` when the full
     * constructor props are known, otherwise the field's current values are consulted.
     */
    public static applyMobileDefaults(textbox: Textbox, props?: Partial<TextboxProps>): void {
        const explicit = <K extends keyof TextboxProps>(key: K): Partial<TextboxProps>[K] =>
            props != null ? props[key] : (textbox as unknown as Partial<TextboxProps>)[key];
        const tokens = MobileDesignTokens.textField;
        if (explicit("fontSize") == null)
            textbox.fontSize = tokens.fontSize;

        const apply = () => {
            const inputDiv = textbox._inputDiv;
            if (inputDiv == null)
                return;
            const hasExplicitHeight = explicit("height") != null || explicit("minHeight") != null;
            if (!hasExplicitHeight)
                inputDiv.style.minHeight = `${tokens.height}px`;
            if (explicit("borderRadius") == null)
                inputDiv.style.borderRadius = `${tokens.radius}px`;
            // Let the <input> fill the taller box instead of keeping the desktop 34px rule from the stylesheet.
            if (textbox._input != null && explicit("height") == null && !textbox.multiline)
                textbox._input.style.height = "100%";
        };
        apply();
        // The input elements can be (re)created after construction, so apply again once the field is on screen.
        textbox.addMountListener(apply);
    }
}

ComponentTypes.registerAppComponentType("textbox", {
    constructor: AppTextbox.prototype.constructor
});
